import { observable, computed, action, makeObservable, toJS } from 'mobx'
import { TILE_CONTENT } from '@mixtiles/web-backend-shared'
import { SinglePhotoTileStore } from './SinglePhotoTileStore'
import { type AiTemplate } from '../TilesStore.types'
import type { PhotoBankStore } from '../../photoBankStore/PhotoBankStore'
import type { AIGenPhotoTileData, AiTileGenerationStatus } from './tileSchemas'
import { type TilePatchMode } from '../TilePatch'
import { type TilePersistenceDisposition } from './BaseTileStore'
import { type PhotoFaceData } from 'components/PhotoBank/PhotoBank.types'

const SAVE_TILE_KEYS = ['aiTemplate', 'sources', 'generationStatus']
const AI_GEN_PATCHABLE_KEYS_WHITELIST = new Set([
  'aiTemplate',
  'sources',
  'generationStatus',
])

export type AiTileSource = { uid: string; face?: PhotoFaceData }

type CompleteGenerationParams = {
  uid: string
  jobId: string
  totalDurationSeconds: number
}

export class AIGenPhotoTileStore extends SinglePhotoTileStore {
  @observable.deep accessor aiTemplate: AiTemplate

  @observable accessor sources: AiTileSource[] | undefined

  @observable.deep accessor generationStatus: AiTileGenerationStatus | undefined

  constructor(data: AIGenPhotoTileData, photoBankStore: PhotoBankStore) {
    super(data, photoBankStore)
    this.aiTemplate = data.aiTemplate as AiTemplate
    this.sources = data.sources
    this.generationStatus = data.generationStatus
    makeObservable(this)
  }

  @computed
  get isGenerating(): boolean {
    return this.generationStatus !== undefined
  }

  get tileContent(): TILE_CONTENT {
    return TILE_CONTENT.AI_GEN_PHOTO
  }

  @action
  markGenerationStarted(jobId: string): void {
    this.generationStatus = {
      status: 'generating',
      jobId,
      startedAt: Date.now(),
    }
  }

  @action
  completeGeneration({
    uid,
    jobId,
    totalDurationSeconds,
  }: CompleteGenerationParams): void {
    this.uid = uid
    this.aiTemplate.jobId = jobId
    this.aiTemplate.totalDurationSeconds = totalDurationSeconds
    this.generationStatus = undefined
  }

  override hasValidRequiredPhotos(): boolean {
    if (this.generationStatus?.status === 'generating') return true
    return super.hasValidRequiredPhotos()
  }

  // Keep generating placeholders in local storage for refresh recovery, but out
  // of backend order drafts until a result photo exists.
  override getPersistenceState(): TilePersistenceDisposition {
    if (this.generationStatus?.status === 'pending') return 'skip'
    if (this.generationStatus?.status === 'generating') return 'pending'
    return super.getPersistenceState()
  }

  override toTileData(): AIGenPhotoTileData {
    return {
      ...super.toTileData(),
      tileContent: TILE_CONTENT.AI_GEN_PHOTO,
      aiTemplate: toJS(this.aiTemplate),
      sources: toJS(this.sources),
      generationStatus: toJS(this.generationStatus),
    }
  }

  override get persistenceKeys(): string[] {
    return super.persistenceKeys.concat(SAVE_TILE_KEYS)
  }

  protected override getPatchableKeysWhitelist(
    mode: TilePatchMode
  ): Set<string> {
    return new Set([
      ...super.getPatchableKeysWhitelist(mode),
      ...AI_GEN_PATCHABLE_KEYS_WHITELIST,
    ])
  }
}
